import { useCallback, useMemo } from "react";
import { BaseTableV3Pagination } from "./BaseTableV3Pagination";
import { ResponsiveTable } from "./ResponsiveTable";
import { tableRootKey } from "components/BaseTableV3/queryKeys";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { useQuery } from "@tanstack/react-query";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useModalTable } from "hooks/useModalTable";
import { arrayMove } from "@dnd-kit/sortable";
import { TableTop } from "./TableTop";
import { useColumnSize } from "hooks/useColumnSize";

const defaultData = [];

export function ModalTable({
  columns = [],
  queryKey,
  fetchData,
  rowIdKey = "id",
  enableSearch = false,
  headerIcon = null,
  header = null,
  exportTableName,
  downloadParams,
  extraButtons,
}) {
  const {
    columnOrder,
    pagination,
    sorting,
    queryParams,
    setColumnOrder,
    handleSearchChange,
    handleSortChange,
    handlePaginationChange,
  } = useModalTable({ columns });

  const rootKey = useMemo(() => tableRootKey(queryKey), [queryKey]);

  const cacheKeys = useMemo(
    () => [...rootKey, queryParams],
    [rootKey, queryParams]
  );

  const {
    data: queryResult,
    isFetched,
    isError,
  } = useQuery({
    queryKey: cacheKeys,
    queryFn: () => fetchData(queryParams),
    initialData: defaultData,
    keepPreviousData: true,
    enabled: columns.length > 0,
  });

  const table = useReactTable({
    columnResizeMode: "onChange",
    columnResizeDirection: "ltr",
    data: queryResult?.data ?? defaultData,
    rowCount: queryResult?.total,
    enableColumnResizing: true,
    manualSorting: true,
    // With manual sorting, TanStack infers the first direction from the top row
    // of the current page, which changes after each sort and can skip a direction.
    sortDescFirst: false,
    manualPagination: true,
    manualFiltering: true,
    columns,
    defaultColumn: {
      minSize: 60,
      maxSize: 800,
    },
    state: {
      pagination,
      columnOrder,
      sorting,
    },
    getRowId: row => row[rowIdKey],
    onColumnOrderChange: setColumnOrder,
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortChange,
    getCoreRowModel: getCoreRowModel(),
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  const columnSizeVars = useColumnSize({ table, columns });

  const handleDragEnd = useCallback(
    event => {
      const { active, over } = event;

      if (active && over && active.id !== over.id) {
        setColumnOrder(columnOrder => {
          const oldIndex = columnOrder.indexOf(active.id);
          const newIndex = columnOrder.indexOf(over.id);
          const newColumnOrder = arrayMove(columnOrder, oldIndex, newIndex);
          return newColumnOrder;
        });
      }
    },
    [setColumnOrder]
  );
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflowY: "auto",
      }}
    >
      <TableTop
        headerIcon={headerIcon}
        header={header}
        enableSearch={enableSearch}
        searchQuery={queryParams.search}
        updateSearch={handleSearchChange}
        exportTableName={exportTableName}
        tableSettings={{
          visibleColumns: columnOrder,
          sort: sorting,
        }}
        downloadParams={downloadParams}
        extra={queryResult?.extra}
        extraButtons={extraButtons}
        isFetched={isFetched}
      />
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <ResponsiveTable
          table={table}
          columnSizeVars={columnSizeVars}
          columnOrder={columnOrder}
          isFetched={isFetched}
          isError={isError}
          queryResult={queryResult}
        />
      </DndContext>
      <BaseTableV3Pagination table={table} />
    </div>
  );
}
